import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { ScanContextService, ScanContext, Period } from '../../../core/services/scan-context.service';
import { ToastService } from '../../../core/services/toast.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';

@Component({
  selector: 'app-context-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './context-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./context-modal.component.scss']
})
export class ContextModalComponent implements OnInit, OnDestroy {
  @Input() contexts: { id: string; name: string }[] = [];
  @Input() queueId: string = '';
  @Output() close = new EventEmitter<void>();
  @Output() contextModified = new EventEmitter<void>();

  private scanContextService = inject(ScanContextService);
  private toastService = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  selectedContext = signal<ScanContext | null>(null);
  viewMode = signal<'list' | 'edit' | 'view' | 'create'>('list');
  contextForm: FormGroup;
  loading = signal(false);
  fullContexts = signal<ScanContext[]>([]);
  showDeleteConfirm = signal(false);
  contextToDelete = signal<{ id: string; name: string } | null>(null);
  showDeletePeriodConfirm = signal(false);
  periodIndexToDelete = signal<number | null>(null);
  hasModifications = false;
  periodStats = signal<Map<string, any>>(new Map());

  constructor(private fb: FormBuilder) {
    this.contextForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      periods: this.fb.array([])
    });
  }

  get periodsArray(): FormArray {
    return this.contextForm.get('periods') as FormArray;
  }

  ngOnInit() {
    document.body.style.overflow = 'hidden';
    this.loadContexts();
  }

  ngOnDestroy() {
    document.body.style.overflow = '';
  }

  onClose() {
    if (this.hasModifications) {
      this.contextModified.emit();
    }
    this.close.emit();
  }

  loadContexts() {
    if (!this.queueId) return;
    
    this.loading.set(true);
    this.scanContextService.getContexts(this.queueId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (contexts) => {
          this.fullContexts.set(contexts);
          this.loading.set(false);
        },
        error: (err) => {
          console.error('Failed to load contexts:', err);
          this.toastService.show('Erreur', 'error', 3000, 'Impossible de charger les contextes');
          this.loading.set(false);
        }
      });
  }

  editContext(context: { id: string; name: string }) {
    const fullContext = this.fullContexts().find(c => c.id === context.id);
    if (!fullContext) return;
    
    this.selectedContext.set(fullContext);
    this.viewMode.set('edit');
    this.contextForm.patchValue({
      name: fullContext.name,
      description: fullContext.description || ''
    });
    
    this.periodsArray.clear();
    fullContext.periods?.forEach(period => {
      this.periodsArray.push(this.fb.group({
        id: [period.id],
        name: [period.name, Validators.required],
        startDate: [period.startDate, Validators.required],
        endDate: [period.endDate, Validators.required],
        comment: [period.comment || '']
      }, { validators: this.dateRangeValidator }));
    });
  }

  viewContext(context: { id: string; name: string }) {
    const fullContext = this.fullContexts().find(c => c.id === context.id);
    if (fullContext) {
      this.selectedContext.set(fullContext);
      this.viewMode.set('view');
      this.loadPeriodStats(fullContext);
    }
  }

  private loadPeriodStats(context: ScanContext) {
    if (!context.periods || context.periods.length === 0) return;

    context.periods.forEach(period => {
      this.scanContextService.getPeriodStats(this.queueId, context.id, period.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (stats) => {
            const currentStats = this.periodStats();
            currentStats.set(period.id, stats);
            this.periodStats.set(new Map(currentStats));
          },
          error: (err) => {
            console.error('Failed to load period stats:', err);
          }
        });
    });
  }

  getPeriodStats(periodId: string) {
    return this.periodStats().get(periodId);
  }

  createContext() {
    this.selectedContext.set(null);
    this.viewMode.set('create');
    this.contextForm.reset();
    this.periodsArray.clear();
  }

  backToList() {
    this.selectedContext.set(null);
    this.viewMode.set('list');
    this.contextForm.reset();
    this.periodsArray.clear();
  }

  addPeriod() {
    const periodGroup = this.fb.group({
      id: [null],
      name: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      comment: ['']
    }, { validators: this.dateRangeValidator });
    this.periodsArray.push(periodGroup);
  }

  private dateRangeValidator(group: any) {
    const start = group.get('startDate')?.value;
    const end = group.get('endDate')?.value;
    if (start && end && start > end) {
      return { dateRange: true };
    }
    return null;
  }

  removePeriod(index: number) {
    this.periodIndexToDelete.set(index);
    this.showDeletePeriodConfirm.set(true);
  }

  confirmRemovePeriod() {
    const index = this.periodIndexToDelete();
    if (index !== null) {
      this.periodsArray.removeAt(index);
    }
    this.showDeletePeriodConfirm.set(false);
    this.periodIndexToDelete.set(null);
  }

  cancelRemovePeriod() {
    this.showDeletePeriodConfirm.set(false);
    this.periodIndexToDelete.set(null);
  }

  confirmDeleteContext(context: { id: string; name: string }) {
    this.contextToDelete.set(context);
    this.showDeleteConfirm.set(true);
  }

  cancelDelete() {
    this.showDeleteConfirm.set(false);
    this.contextToDelete.set(null);
  }

  deleteContext() {
    const context = this.contextToDelete();
    if (!context || !this.queueId) return;

    this.loading.set(true);
    this.scanContextService.deleteContext(this.queueId, context.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.hasModifications = true;
          this.toastService.show('Situation supprimée', 'success', 3000, 'La situation a été supprimée avec succès');
          this.loading.set(false);
          this.showDeleteConfirm.set(false);
          this.contextToDelete.set(null);
          this.loadContexts();
        },
        error: (err) => {
          console.error('Failed to delete context:', err);
          const errorMsg = err?.error?.message || err?.error?.error || 'Impossible de supprimer le contexte';
          this.toastService.show('Erreur', 'error', 5000, errorMsg);
          this.loading.set(false);
        }
      });
  }

  saveContext() {
    if (!this.queueId) return;

    // Marquer tous les champs comme touchés pour afficher les erreurs
    this.contextForm.markAllAsTouched();
    this.periodsArray.controls.forEach(control => control.markAllAsTouched());

    // Vérifier les champs requis du contexte
    if (this.contextForm.get('name')?.invalid) {
      this.toastService.show('Erreur', 'error', 3000, 'Le nom de la situation est requis');
      return;
    }

    if (this.periodsArray.length === 0) {
      this.toastService.show('Erreur', 'error', 3000, 'Veuillez ajouter au moins une période');
      const addButton = document.querySelector('.acc-btn-add');
      if (addButton) {
        addButton.classList.add('shake-animation');
        setTimeout(() => addButton.classList.remove('shake-animation'), 500);
      }
      return;
    }

    // Vérifier les erreurs de plage de dates
    for (let i = 0; i < this.periodsArray.controls.length; i++) {
      const period = this.periodsArray.controls[i];
      if (period.hasError('dateRange')) {
        this.toastService.show('Erreur', 'error', 4000, 'La date de fin doit être après la date de début');
        return;
      }
      if (period.invalid) {
        this.toastService.show('Erreur', 'error', 3000, 'Veuillez remplir tous les champs obligatoires des périodes');
        return;
      }
    }

    // Vérifier les chevauchements de périodes
    const periods = this.periodsArray.value;
    for (let i = 0; i < periods.length; i++) {
      for (let j = i + 1; j < periods.length; j++) {
        const p1Start = new Date(periods[i].startDate);
        const p1End = new Date(periods[i].endDate);
        const p2Start = new Date(periods[j].startDate);
        const p2End = new Date(periods[j].endDate);

        if ((p1Start <= p2End && p1End >= p2Start)) {
          this.toastService.show('Erreur', 'error', 5000, `Les périodes "${periods[i].name}" et "${periods[j].name}" se chevauchent`);
          return;
        }
      }
    }

    const formValue = this.contextForm.value;
    const isEdit = this.viewMode() === 'edit';
    const contextId = this.selectedContext()?.id;

    this.loading.set(true);

    const periodsData = this.periodsArray.value;
    const firstPeriod = periodsData[0];

    if (isEdit && contextId) {
      // Mode édition : update contexte puis save toutes les périodes
      const contextData = {
        name: formValue.name,
        description: formValue.description
      };
      
      this.scanContextService.updateContext(this.queueId, contextId, contextData)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (savedContext) => {
            this.savePeriods(savedContext.id);
          },
          error: (err) => {
            console.error('Failed to save context:', err);
            this.toastService.show('Erreur', 'error', 3000, 'Impossible de sauvegarder le contexte');
            this.loading.set(false);
          }
        });
    } else {
      // Mode création : create contexte avec première période
      const contextData = {
        name: formValue.name,
        description: formValue.description,
        period: {
          name: firstPeriod.name,
          startDate: firstPeriod.startDate,
          endDate: firstPeriod.endDate,
          comment: firstPeriod.comment
        }
      };
      
      this.scanContextService.createContext(this.queueId, contextData)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (savedContext) => {
            // Sauvegarder les périodes restantes (à partir de l'index 1)
            if (periodsData.length > 1) {
              this.saveRemainingPeriods(savedContext.id, periodsData.slice(1));
            } else {
              this.hasModifications = true;
              this.toastService.show('Situation créée', 'success', 3000, 'La nouvelle situation a été ajoutée avec succès');
              this.loading.set(false);
              this.loadContexts();
              this.backToList();
            }
          },
          error: (err) => {
            console.error('Failed to create context:', err);
            this.toastService.show('Erreur', 'error', 3000, 'Impossible de créer le contexte');
            this.loading.set(false);
          }
        });
    }
  }

  private saveRemainingPeriods(contextId: string, periods: any[]) {
    let completed = 0;
    const total = periods.length;

    periods.forEach((period: any) => {
      const periodData = {
        name: period.name,
        startDate: period.startDate,
        endDate: period.endDate,
        comment: period.comment
      };

      this.scanContextService.createPeriod(this.queueId, contextId, periodData)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            completed++;
            if (completed === total) {
              this.hasModifications = true;
              this.toastService.show('Situation créée', 'success', 3000, 'La nouvelle situation et ses périodes ont été ajoutées');
              this.loading.set(false);
              this.loadContexts();
              this.backToList();
            }
          },
          error: (err) => {
            console.error('Failed to save period:', err);
            const errorMsg = err?.error?.message || err?.error?.error || 'Erreur lors de la sauvegarde d\'une période';
            this.toastService.show('Erreur', 'error', 5000, errorMsg);
            this.loading.set(false);
          }
        });
    });
  }

  private savePeriods(contextId: string) {
    const periods = this.periodsArray.value;
    let completed = 0;
    const total = periods.length;

    if (total === 0) {
      this.toastService.show('Succès', 'success', 3000, 'Contexte sauvegardé');
      this.loading.set(false);
      this.loadContexts();
      this.backToList();
      return;
    }

    periods.forEach((period: any) => {
      const periodData = {
        name: period.name,
        startDate: period.startDate,
        endDate: period.endDate,
        comment: period.comment
      };

      // Si period.id existe ET n'est pas null, c'est un UPDATE, sinon c'est un CREATE
      const periodObs = (period.id && period.id !== null)
        ? this.scanContextService.updatePeriod(this.queueId, contextId, period.id, periodData)
        : this.scanContextService.createPeriod(this.queueId, contextId, periodData);

      periodObs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          completed++;
          if (completed === total) {
            this.hasModifications = true;
            this.toastService.show('Situation mise à jour', 'success', 3000, 'Les modifications ont été enregistrées avec succès');
            this.loading.set(false);
            this.loadContexts();
            this.backToList();
          }
        },
        error: (err) => {
          console.error('Failed to save period:', err);
          const errorMsg = err?.error?.message || err?.error?.error || 'Erreur lors de la sauvegarde d\'une période';
          this.toastService.show('Erreur', 'error', 5000, errorMsg);
          this.loading.set(false);
        }
      });
    });
  }
}
